import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Button } from "./components/ui/button";
import { Toaster } from "./components/ui/sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dream-play-rise/artifacts/iixhvbkm_image.png";
const AARAV_IMG = "https://customer-assets.emergentagent.com/job_dream-play-rise/artifacts/imgxz563_image.png";
const IRA_IMG = "https://customer-assets.emergentagent.com/job_dream-play-rise/artifacts/xur1ydrg_Screenshot%202026-04-19%20224514.png";
const AAHANAA_IMG = "https://customer-assets.emergentagent.com/job_dream-play-rise/artifacts/vykg033r_image.png";

/* ─── NAVIGATION ─── */
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const navLinks = [
    { label: "About", id: "about" },
    { label: "Pathway", id: "opportunity" },
    { label: "Tournament", id: "tournament" },
    { label: "Our Team", id: "team" },
    { label: "Get Involved", id: "get-involved" },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-xl shadow-lg" : "bg-white/60 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <img src={LOGO_URL} alt="Ball and Beyond" className="w-12 h-12 object-contain mix-blend-multiply" />
            <span className="font-['Outfit'] font-extrabold text-xl text-[#0B1B3D] hidden sm:block">BALL & BEYOND</span>
          </div>
          <div className="hidden lg:flex items-center space-x-6">
            {navLinks.map((l) => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="text-[#0B1B3D] hover:text-[#1f3a1b] font-medium transition-colors text-sm">{l.label}</button>
            ))}
            <Button data-testid="nav-register-button" onClick={() => scrollTo("get-involved")} className="bg-[#1f3a1b] hover:bg-[#0B1B3D] text-white rounded-full px-6 py-5 font-semibold text-sm">Register Team</Button>
          </div>
          <button className="lg:hidden text-[#0B1B3D]" onClick={() => setMobileOpen(!mobileOpen)}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={mobileOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} /></svg>
          </button>
        </div>
        {mobileOpen && (
          <div className="lg:hidden pb-4 space-y-2">
            {navLinks.map((l) => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="block w-full text-left py-2 px-4 text-[#0B1B3D] hover:bg-[#F4F7F4] rounded-lg font-medium">{l.label}</button>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

/* ─── FOOTBALL ANIMATION (replaces Spline) ─── */
const FootballAnimation = () => {
  return (
    <div className="relative w-full h-full flex items-center justify-center overflow-hidden" data-testid="football-animation">
      <svg viewBox="0 0 600 500" className="w-full h-full max-w-2xl" xmlns="http://www.w3.org/2000/svg">
        <defs>
          {/* Goal net pattern */}
          <pattern id="netPattern" x="0" y="0" width="18" height="18" patternUnits="userSpaceOnUse">
            <path d="M 18 0 L 0 0 0 18" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.8" />
            <path d="M 0 0 L 18 18 M 18 0 L 0 18" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.4" />
          </pattern>

          {/* Ball gradient */}
          <radialGradient id="ballGrad" cx="35%" cy="35%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="70%" stopColor="#f0f0f0" />
            <stop offset="100%" stopColor="#c0c0c0" />
          </radialGradient>

          {/* Ground shadow */}
          <radialGradient id="groundShadow" cx="50%" cy="50%">
            <stop offset="0%" stopColor="rgba(0,0,0,0.4)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </radialGradient>
        </defs>

        {/* Ground line */}
        <line x1="0" y1="420" x2="600" y2="420" stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeDasharray="6 6" />

        {/* Goal (back layer - net) */}
        <g>
          {/* Net back */}
          <rect x="330" y="180" width="240" height="220" fill="url(#netPattern)" opacity="0.9" />
          <rect x="330" y="180" width="240" height="220" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />

          {/* Left side net (perspective) */}
          <polygon points="330,180 300,200 300,380 330,400" fill="url(#netPattern)" opacity="0.6" />

          {/* Right side net (perspective) */}
          <polygon points="570,180 600,200 600,380 570,400" fill="url(#netPattern)" opacity="0.6" />

          {/* Top net (perspective) */}
          <polygon points="330,180 300,200 600,200 570,180" fill="url(#netPattern)" opacity="0.5" />

          {/* Goal posts */}
          <rect x="326" y="176" width="8" height="228" fill="#ffffff" opacity="0.95" rx="2" />
          <rect x="566" y="176" width="8" height="228" fill="#ffffff" opacity="0.95" rx="2" />
          <rect x="326" y="176" width="248" height="8" fill="#ffffff" opacity="0.95" rx="2" />

          {/* Net ripple effect on ball impact */}
          <motion.circle
            cx="450" cy="290" r="8" fill="none" stroke="rgba(236,226,195,0.8)" strokeWidth="2"
            initial={{ opacity: 0, r: 8 }}
            animate={{ opacity: [0, 0.8, 0], r: [8, 60, 80] }}
            transition={{ duration: 3, repeat: Infinity, times: [0.5, 0.55, 0.7], ease: "easeOut" }}
          />
          <motion.circle
            cx="450" cy="290" r="8" fill="none" stroke="rgba(236,226,195,0.5)" strokeWidth="1.5"
            initial={{ opacity: 0, r: 8 }}
            animate={{ opacity: [0, 0.6, 0], r: [8, 45, 65] }}
            transition={{ duration: 3, repeat: Infinity, times: [0.52, 0.57, 0.72], ease: "easeOut" }}
          />
        </g>

        {/* Girl figure (silhouette style with details) */}
        <motion.g
          animate={{ x: [0, 0, 3, -3, 0], y: [0, -2, 0, 0, 0] }}
          transition={{ duration: 3, repeat: Infinity, times: [0, 0.15, 0.2, 0.3, 1] }}
        >
          {/* Body reference position: around x=90, y=280 */}
          
          {/* Ponytail */}
          <motion.path
            d="M 82 218 Q 68 225 65 245 Q 63 260 70 268"
            stroke="#2d1810" strokeWidth="8" fill="none" strokeLinecap="round"
            animate={{ d: [
              "M 82 218 Q 68 225 65 245 Q 63 260 70 268",
              "M 82 218 Q 70 228 68 250 Q 67 265 74 272",
              "M 82 218 Q 68 225 65 245 Q 63 260 70 268"
            ]}}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          
          {/* Head */}
          <circle cx="90" cy="220" r="16" fill="#e8b89a" />
          {/* Hair (covers most of forehead + sides) */}
          <path d="M 72 224 Q 70 202 90 198 Q 110 202 108 224 Q 108 218 104 215 L 104 220 Q 100 214 90 213 Q 80 214 76 220 L 76 215 Q 72 218 72 224 Z" fill="#2d1810" />
          {/* Side hair strands */}
          <path d="M 74 222 Q 71 232 73 240" stroke="#2d1810" strokeWidth="3" fill="none" strokeLinecap="round"/>
          <path d="M 106 222 Q 109 232 107 240" stroke="#2d1810" strokeWidth="3" fill="none" strokeLinecap="round"/>
          {/* Bangs */}
          <path d="M 78 218 Q 90 216 102 218 L 100 222 Q 90 220 80 222 Z" fill="#2d1810" />
          {/* Face detail (eye + smile) */}
          <circle cx="94" cy="224" r="1.3" fill="#2d1810" />
          <circle cx="86" cy="224" r="1.3" fill="#2d1810" />
          <path d="M 88 230 Q 90 232 92 230" stroke="#2d1810" strokeWidth="0.8" fill="none" strokeLinecap="round"/>

          {/* Neck */}
          <rect x="86" y="234" width="8" height="6" fill="#e8b89a" />

          {/* Torso (jersey - Ball and Beyond green) */}
          <path d="M 74 240 L 106 240 L 108 285 L 72 285 Z" fill="#1f3a1b" />
          {/* Jersey number */}
          <text x="90" y="270" textAnchor="middle" fill="#ece2c3" fontSize="14" fontWeight="bold" fontFamily="Outfit">7</text>
          {/* Jersey stripe */}
          <line x1="72" y1="252" x2="108" y2="252" stroke="#ece2c3" strokeWidth="1.5" />

          {/* Left arm (extended for balance) */}
          <motion.path
            d="M 74 244 L 55 268 L 50 290"
            stroke="#e8b89a" strokeWidth="7" fill="none" strokeLinecap="round"
            animate={{ d: [
              "M 74 244 L 55 268 L 50 290",
              "M 74 244 L 50 260 L 40 275",
              "M 74 244 L 55 268 L 50 290"
            ]}}
            transition={{ duration: 3, repeat: Infinity, times: [0, 0.15, 1], ease: "easeInOut" }}
          />

          {/* Right arm (swinging with kick) */}
          <motion.path
            d="M 106 244 L 125 265 L 130 285"
            stroke="#e8b89a" strokeWidth="7" fill="none" strokeLinecap="round"
            animate={{ d: [
              "M 106 244 L 125 265 L 130 285",
              "M 106 244 L 130 258 L 140 268",
              "M 106 244 L 125 265 L 130 285"
            ]}}
            transition={{ duration: 3, repeat: Infinity, times: [0, 0.15, 1], ease: "easeInOut" }}
          />

          {/* Shorts */}
          <path d="M 72 285 L 108 285 L 110 315 L 70 315 Z" fill="#0B1B3D" />

          {/* Left leg (planted / standing) */}
          <motion.path
            d="M 78 315 L 76 355 L 74 395"
            stroke="#e8b89a" strokeWidth="9" fill="none" strokeLinecap="round"
            animate={{ 
              d: [
                "M 78 315 L 76 355 L 74 395",
                "M 78 315 L 74 358 L 70 398",
                "M 78 315 L 76 355 L 74 395"
              ]
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          {/* Left shoe */}
          <ellipse cx="76" cy="400" rx="10" ry="4" fill="#0B1B3D" />

          {/* Right leg (kicking motion) */}
          <motion.path
            d="M 100 315 L 120 340 L 145 355"
            stroke="#e8b89a" strokeWidth="9" fill="none" strokeLinecap="round"
            animate={{ d: [
              "M 100 315 L 110 355 L 108 395",
              "M 100 315 L 130 340 L 165 335",
              "M 100 315 L 145 320 L 195 305",
              "M 100 315 L 130 350 L 130 390",
              "M 100 315 L 110 355 L 108 395"
            ]}}
            transition={{ duration: 3, repeat: Infinity, times: [0, 0.1, 0.2, 0.35, 1], ease: "easeInOut" }}
          />
          {/* Right shoe */}
          <motion.ellipse
            cx="145" cy="355" rx="12" ry="4" fill="#0B1B3D"
            animate={{ 
              cx: [108, 165, 195, 130, 108],
              cy: [400, 335, 305, 390, 400]
            }}
            transition={{ duration: 3, repeat: Infinity, times: [0, 0.1, 0.2, 0.35, 1], ease: "easeInOut" }}
          />

          {/* Standing foot shadow */}
          <ellipse cx="76" cy="410" rx="16" ry="3" fill="url(#groundShadow)" />
        </motion.g>

        {/* Football (kicked from girl to goal) */}
        <motion.g
          animate={{
            x: [0, 20, 150, 320, 380, 380, 0],
            y: [0, -30, -90, -60, 20, 20, 0],
            rotate: [0, 180, 540, 900, 1080, 1080, 0]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            times: [0, 0.1, 0.2, 0.35, 0.5, 0.55, 1],
            ease: [0.25, 0.1, 0.25, 1]
          }}
        >
          <g transform="translate(180, 390)">
            <circle cx="0" cy="0" r="14" fill="url(#ballGrad)" stroke="#333" strokeWidth="1" />
            <polygon points="0,-10 8,-3 5,7 -5,7 -8,-3" fill="#0B1B3D" opacity="0.9" />
            <polygon points="0,-10 8,-3 12,-8 8,-14 -2,-13" fill="#333" opacity="0.7" />
            <polygon points="8,-3 5,7 12,10 14,2" fill="#333" opacity="0.7" />
            <polygon points="-8,-3 -5,7 -12,10 -14,2" fill="#333" opacity="0.7" />
          </g>
        </motion.g>

        {/* Ball shadow on ground */}
        <motion.ellipse
          cx="180" cy="415" rx="12" ry="3" fill="rgba(0,0,0,0.3)"
          animate={{
            cx: [180, 200, 330, 500, 560, 560, 180],
            rx: [12, 8, 4, 6, 14, 14, 12],
            opacity: [0.5, 0.3, 0.1, 0.2, 0.5, 0.5, 0.5]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            times: [0, 0.1, 0.2, 0.35, 0.5, 0.55, 1],
            ease: [0.25, 0.1, 0.25, 1]
          }}
        />

        {/* Impact flash on goal */}
        <motion.circle
          cx="450" cy="290" r="20" fill="rgba(236,226,195,0.6)"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0, 1, 0, 0] }}
          transition={{ duration: 3, repeat: Infinity, times: [0, 0.48, 0.5, 0.55, 1] }}
        />

        {/* GOAL! text flash */}
        <motion.text
          x="450" y="150" textAnchor="middle" fill="#ece2c3"
          fontSize="42" fontWeight="900" fontFamily="Outfit"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ 
            opacity: [0, 0, 1, 1, 0, 0],
            scale: [0.5, 0.5, 1.2, 1, 0.8, 0.5]
          }}
          transition={{ duration: 3, repeat: Infinity, times: [0, 0.5, 0.55, 0.75, 0.85, 1] }}
        >
          GOAL!
        </motion.text>

        {/* Motion lines behind ball during flight */}
        <motion.g
          animate={{ opacity: [0, 0.6, 0.8, 0.4, 0, 0, 0] }}
          transition={{ duration: 3, repeat: Infinity, times: [0, 0.15, 0.25, 0.4, 0.5, 0.55, 1] }}
        >
          <motion.line
            x1="180" y1="380" x2="200" y2="360" stroke="rgba(236,226,195,0.6)" strokeWidth="2" strokeLinecap="round"
            animate={{ 
              x1: [180, 180, 220, 340, 400, 400, 180],
              y1: [380, 380, 300, 320, 380, 380, 380],
              x2: [200, 200, 260, 380, 440, 440, 200],
              y2: [360, 360, 290, 305, 375, 375, 360]
            }}
            transition={{ duration: 3, repeat: Infinity, times: [0, 0.1, 0.2, 0.35, 0.5, 0.55, 1] }}
          />
        </motion.g>

        {/* Ambient particles */}
        {[...Array(8)].map((_, i) => (
          <motion.circle
            key={i}
            cx={80 + i * 60}
            cy={100 + (i % 3) * 40}
            r="1.5"
            fill="rgba(236,226,195,0.4)"
            animate={{
              cy: [100 + (i % 3) * 40 - 10, 100 + (i % 3) * 40 + 10, 100 + (i % 3) * 40 - 10],
              opacity: [0.2, 0.6, 0.2]
            }}
            transition={{ duration: 2 + i * 0.3, repeat: Infinity, ease: "easeInOut", delay: i * 0.2 }}
          />
        ))}
      </svg>
    </div>
  );
};

/* ─── HERO ─── */
const Hero = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B1B3D] via-[#132d1a] to-[#1f3a1b]" />
      <div className="absolute inset-0 field-lines opacity-30" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="space-y-8">
            <div className="space-y-4">
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                className="text-sm font-bold uppercase tracking-[0.2em] text-[#ece2c3]">
                U17 Girls' Football Tournament
              </motion.p>
              <h1 className="font-['Outfit'] text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight uppercase text-white leading-tight">
                Dream.<br />Play.<br />
                <span className="text-[#757e40]">Rise.</span>
              </h1>
              <p className="text-base md:text-lg leading-relaxed text-white/70 max-w-lg">
                Ball and Beyond is a U17 7v7 football competition for girls in Delhi-NCR, supported by BBFS and Garhwal United. It will bring together players from all backgrounds and give them a platform to showcase their talent, compete and gain real exposure.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Button data-testid="hero-register-button" onClick={() => document.getElementById("get-involved")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-[#1f3a1b] hover:bg-[#757e40] text-white rounded-full font-semibold px-8 py-6 text-lg border border-[#757e40]/30">
                Register Now
              </Button>
              <Button data-testid="hero-volunteer-button" onClick={() => document.getElementById("get-involved")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-transparent text-white border-2 border-white/30 hover:border-white hover:bg-white/10 rounded-full px-8 py-6 font-semibold text-lg">
                Volunteer
              </Button>
              <Button data-testid="hero-partner-button" onClick={() => document.getElementById("sponsors")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-transparent text-white border-2 border-white/30 hover:border-white hover:bg-white/10 rounded-full px-8 py-6 font-semibold text-lg">
                Partner With Us
              </Button>
            </div>
            <div className="flex items-center space-x-6 pt-4">
              <p className="text-xs text-white/50 mb-1">Supported by</p>
              <span className="font-bold text-white/80">BBFS</span>
              <span className="text-white/30">|</span>
              <span className="font-bold text-white/80">Garhwal United</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.3 }}
            className="relative h-[400px] lg:h-[500px]">
            <FootballAnimation />
          </motion.div>
        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" className="w-full"><path d="M0,80 C360,120 720,40 1080,80 C1260,100 1380,60 1440,80 L1440,120 L0,120 Z" fill="white"/></svg>
      </div>
    </section>
  );
};

/* ─── ABOUT ─── */
const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section id="about" data-testid="about-section" ref={ref} className="py-24 md:py-32 bg-white diagonal-stripes">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="text-center space-y-6">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#1f3a1b]">About the Tournament</p>
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight text-[#0B1B3D]">What is Ball and Beyond?</h2>
          <p className="text-base md:text-lg leading-relaxed text-[#4B5563] max-w-3xl mx-auto">
            Ball and Beyond is a U17 7v7 football competition for girls in Delhi-NCR, supported by BBFS and Garhwal United. It will bring together players from all backgrounds and give them a platform to showcase their talent, compete and gain real exposure. It's not just a tournament, it's a platform that connects players to professional pathways, and future opportunities in football.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

/* ─── OPPORTUNITY PATHWAY ─── */
const OpportunityPathway = () => {
  const steps = [
    { title: "Play", desc: "Compete in professional-level matches with experienced referees and coaches", color: "#1f3a1b" },
    { title: "Scouts", desc: "Scouts attend knockout matches to identify standout talent", color: "#2a4d24" },
    { title: "Get Selected", desc: "Top performers are identified and selected into an elite player pool", color: "#375e30" },
    { title: "Train", desc: "Access one week of specialized training sessions with the Garhwal United youth squad", color: "#456f3c" },
    { title: "Showcase & Earn", desc: "Unlock pathways to scholarships and training in BBFS and Garhwal United squads", color: "#757e40" },
  ];

  return (
    <section id="opportunity" data-testid="opportunity-pathway-section" className="py-24 md:py-32 bg-[#0B1B3D] relative overflow-hidden">
      <div className="absolute inset-0 field-lines opacity-10" />
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-16">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#ece2c3]">Your Journey</p>
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight text-white mt-4">Opportunity Pathway</h2>
          <p className="text-base md:text-lg leading-relaxed text-white/60 mt-6 max-w-2xl mx-auto">
            This tournament offers a professional pathway beyond just matches, a journey from playing to professional opportunities.
          </p>
        </motion.div>

        {/* Arrow flow */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-0">
          {steps.map((step, i) => (
            <React.Fragment key={i}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                viewport={{ once: true }}
                className="relative rounded-2xl p-6 text-center w-full md:w-48 flex-shrink-0"
                style={{ backgroundColor: step.color }}
              >
                <div className="text-3xl mb-3 w-12 h-12 bg-white/15 rounded-full flex items-center justify-center mx-auto font-['Outfit'] font-bold text-white text-lg">
                  {i + 1}
                </div>
                <h3 className="font-['Outfit'] text-lg font-bold text-white mb-2">{step.title}</h3>
                <p className="text-xs text-white/70 leading-relaxed">{step.desc}</p>
              </motion.div>
              {i < steps.length - 1 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: i * 0.15 + 0.1 }}
                  viewport={{ once: true }}
                  className="text-[#757e40] flex-shrink-0 hidden md:block"
                >
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/></svg>
                </motion.div>
              )}
              {i < steps.length - 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: i * 0.15 + 0.1 }}
                  viewport={{ once: true }}
                  className="text-[#757e40] md:hidden"
                >
                  <svg className="w-8 h-8 rotate-90" fill="currentColor" viewBox="0 0 24 24"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/></svg>
                </motion.div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── COUNTDOWN ─── */
const Countdown = () => {
  const tournamentDate = new Date("2026-07-12T00:00:00");
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const diff = tournamentDate - now;
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((diff / 1000 / 60) % 60),
          seconds: Math.floor((diff / 1000) % 60),
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="bg-[#0B1B3D] rounded-3xl p-8 md:p-10 shadow-xl mb-12 relative overflow-hidden"
      data-testid="countdown-section"
    >
      <div className="absolute inset-0 field-lines opacity-10 pointer-events-none" />
      <div className="relative z-10">
        <div className="text-center mb-6">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#ece2c3] mb-2">Tournament Starts In</p>
          <p className="text-white/60 text-sm">July 12, 2026</p>
        </div>
        <div className="grid grid-cols-4 gap-3 md:gap-6 max-w-3xl mx-auto">
          {[
            { label: "Days", value: timeLeft.days },
            { label: "Hours", value: timeLeft.hours },
            { label: "Minutes", value: timeLeft.minutes },
            { label: "Seconds", value: timeLeft.seconds },
          ].map((item) => (
            <div key={item.label} className="text-center">
              <div className="bg-white/5 rounded-2xl p-4 md:p-6 border border-white/10">
                <div className="text-3xl md:text-5xl font-bold text-[#ece2c3] font-['Outfit']">
                  {String(item.value).padStart(2, "0")}
                </div>
                <div className="text-xs md:text-sm text-white/70 mt-2 uppercase tracking-wide">
                  {item.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

/* ─── TOURNAMENT DETAILS ─── */
const TournamentFormat = () => {
  const stages = ["Group Stage", "Knockout", "Semi-Final", "Final"];

  return (
    <section id="tournament" data-testid="tournament-format-section" className="py-24 md:py-32 bg-[#F4F7F4] grass-pattern">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-12">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#1f3a1b]">How It Works</p>
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight text-[#0B1B3D] mt-4">Tournament Details</h2>
        </motion.div>

        {/* Countdown */}
        <Countdown />

        {/* Stage flow with arrows */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-12">
          {stages.map((stage, i) => (
            <React.Fragment key={i}>
              <div className="bg-[#1f3a1b] text-white px-6 py-4 rounded-xl text-center font-['Outfit'] font-bold text-sm sm:text-base shadow-lg min-w-[130px]">
                {stage}
              </div>
              {i < stages.length - 1 && (
                <>
                  <svg className="w-6 h-6 text-[#1f3a1b] hidden sm:block flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/></svg>
                  <svg className="w-6 h-6 text-[#1f3a1b] sm:hidden rotate-90 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/></svg>
                </>
              )}
            </React.Fragment>
          ))}
        </motion.div>

        {/* Info cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { label: "Teams", value: "10+", sub: "Multiple teams competing" },
            { label: "Format", value: "7v7", sub: "Fast-paced football" },
            { label: "Match Duration", value: "25-30 min", sub: "Per match" },
            { label: "Tournament", value: "1 Day", sub: "One-day event" },
            { label: "Prizes", value: "Prizes", sub: "For winners" },
            { label: "Opportunities", value: "Scouting", sub: "For standout players" },
          ].map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.08 }} viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 text-center border border-[#1f3a1b]/5">
              <div className="text-2xl md:text-3xl font-bold text-[#1f3a1b] font-['Outfit'] mb-1">{item.value}</div>
              <div className="text-sm font-semibold text-[#0B1B3D] mb-1">{item.label}</div>
              <div className="text-xs text-[#4B5563]">{item.sub}</div>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.3 }} viewport={{ once: true }}
          className="mt-10">
          <div className="bg-white rounded-3xl p-8 shadow-md border border-[#1f3a1b]/5 max-w-2xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <svg className="w-6 h-6 text-[#1f3a1b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#1f3a1b]">Tournament Venue</p>
            </div>
            <h3 className="font-['Outfit'] text-2xl md:text-3xl font-bold text-[#0B1B3D] mb-4">
              Delhi-NCR
            </h3>
            <p className="text-base text-[#4B5563] mb-6">
              Join us at our official tournament ground. Click below to view the exact location and directions.
            </p>
            <Button
              data-testid="venue-button"
              onClick={() => window.open("https://maps.app.goo.gl/HEqJ5vYdj6JTKASV8", "_blank")}
              className="bg-[#1f3a1b] hover:bg-[#0B1B3D] text-white rounded-full px-8 py-6 text-lg font-semibold"
            >
              View Location on Maps
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ─── AWARDS ─── */
const Awards = () => {
  const awards = [
    { title: "Best Goalkeeper", icon: "🧤" },
    { title: "Best Defender", icon: "⚔️" },
    { title: "Best Midfielder", icon: "⚡" },
    { title: "Best Attacker", icon: "🔥" },
    { title: "Top Scorer", icon: "⚽" },
    { title: "Best Coach", icon: "📋" },
    { title: "Best Player Overall", icon: "🏆" },
  ];

  return (
    <section data-testid="awards-section" className="py-24 md:py-32 bg-white diagonal-stripes">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-16">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#1f3a1b]">Recognition</p>
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight text-[#0B1B3D] mt-4">Awards & Recognition</h2>
          <p className="text-base md:text-lg leading-relaxed text-[#4B5563] mt-6 max-w-2xl mx-auto">
            Recognition based on performance, effort, and potential, celebrating every player's journey.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {awards.map((award, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }} viewport={{ once: true }}
              className="bg-[#F4F7F4] rounded-2xl p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 text-center border border-[#1f3a1b]/5">
              <div className="text-4xl mb-3">{award.icon}</div>
              <h3 className="font-['Outfit'] text-sm font-semibold text-[#0B1B3D]">{award.title}</h3>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── MISSION ─── */
const Mission = () => (
  <section data-testid="mission-section" className="py-24 md:py-32 bg-[#1f3a1b] relative overflow-hidden">
    <div className="absolute inset-0 field-lines opacity-10" />
    <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
        <img src={LOGO_URL} alt="Ball and Beyond" className="w-20 h-20 object-contain mx-auto mb-8 mix-blend-multiply opacity-90" />
        <h2 className="font-['Outfit'] text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-relaxed">
          "We want to give girls who don't usually get these chances the opportunity to play football, enjoy the game, and be seen. Ball and Beyond is about creating real experiences and opening doors through football."
        </h2>
      </motion.div>
    </div>
  </section>
);

/* ─── TEAM ─── */
const Team = () => {
  const coreTeam = [
    { name: "Aarav Mahajan", role: "Founder & Organiser", desc: "15-year-old football player who founded Ball and Beyond, passionate about creating opportunities for young athletes", image: AARAV_IMG },
    { name: "Ira Bhati", role: "Marketing & Design", desc: "Manages social media and digital promotion while crafting visual experiences and leading marketing strategy", image: IRA_IMG },
    { name: "Aahanaa Pradhan", role: "Marketing & Design", desc: "Manages social media and digital promotion while crafting visual experiences and leading marketing strategy", image: AAHANAA_IMG },
  ];

  return (
    <section id="team" data-testid="team-section" className="py-24 md:py-32 bg-[#F4F7F4] grass-pattern">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-16">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#1f3a1b]">Who We Are</p>
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight text-[#0B1B3D] mt-4">Team & Support</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {coreTeam.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: i * 0.1 }} viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-lg transition-all duration-300">
              <div className="w-32 h-32 rounded-full mx-auto mb-6 overflow-hidden border-4 border-[#1f3a1b]/10">
                <img src={m.image} alt={m.name} className="w-full h-full object-cover" />
              </div>
              <h3 className="font-['Outfit'] text-2xl font-semibold text-[#0B1B3D] mb-2 text-center">{m.name}</h3>
              <p className="text-sm font-bold uppercase tracking-wide text-[#1f3a1b] mb-3 text-center">{m.role}</p>
              <p className="text-sm text-[#4B5563] text-center leading-relaxed">{m.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}
          className="bg-[#0B1B3D] rounded-3xl p-8 text-center">
          <h3 className="font-['Outfit'] text-2xl font-semibold text-white mb-4">With Support From</h3>
          <p className="text-base text-white/80 max-w-3xl mx-auto">
            BBFS coaches, experienced referees, medics, volunteers, and dedicated teams handling logistics, outreach, and media coverage to ensure a professional tournament experience.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

/* ─── GET INVOLVED ─── */
const GetInvolved = () => {
  const cards = [
    { title: "Registrations", desc: "Register yourself for the tournament", icon: "⚽", url: "https://forms.gle/rSYnJcnaBqipMXXPA", btn: "Register Now" },
    { title: "Volunteer", desc: "Join our team and help make this tournament a success", icon: "🤝", url: "https://forms.gle/pazjAq2Wi1isai4KA", btn: "Sign Up" },
    { title: "Become a Sponsor", desc: "Partner with us to support grassroots girls' football", icon: "💼", url: "https://forms.gle/SD6Up1Kd3YFzJWaw6", btn: "Get in Touch" },
  ];

  return (
    <section id="get-involved" data-testid="get-involved-section" className="py-24 md:py-32 bg-white diagonal-stripes">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-12">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#1f3a1b]">Join Us</p>
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight text-[#0B1B3D] mt-4">Get Involved</h2>
          <p className="text-base md:text-lg leading-relaxed text-[#4B5563] mt-6 max-w-2xl mx-auto">
            Participation is accessible for all players, volunteers and contributors. Volunteers, contributors and players will receive certificates.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((c, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} viewport={{ once: true }}
              className="bg-[#F4F7F4] rounded-3xl p-8 text-center flex flex-col border border-[#1f3a1b]/5 hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-[#1f3a1b] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">{c.icon}</span>
              </div>
              <h3 className="font-['Outfit'] text-2xl font-semibold text-[#0B1B3D] mb-4">{c.title}</h3>
              <p className="text-base text-[#4B5563] mb-6 flex-grow">{c.desc}</p>
              <Button data-testid={`${c.title.toLowerCase().replace(/\s+/g, '-')}-link`}
                onClick={() => window.open(c.url, '_blank')}
                className="w-full bg-[#1f3a1b] hover:bg-[#0B1B3D] text-white rounded-full py-6 text-lg font-semibold mt-auto">
                {c.btn}
              </Button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── SPONSORS ─── */
const Sponsors = () => (
  <section id="sponsors" data-testid="sponsors-section" className="py-24 bg-[#F4F7F4]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-12">
        <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#1f3a1b]">Our Partners</p>
        <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight text-[#0B1B3D] mt-4">Sponsors & Partners</h2>
        <p className="text-base md:text-lg leading-relaxed text-[#4B5563] mt-6 max-w-2xl mx-auto">
          Join us in supporting grassroots girls' football. Gain visibility, connect with the community, and make a real impact.
        </p>
      </motion.div>
      <div className="mb-12">
        <Marquee gradient={false} speed={40}>
          {["BBFS", "Garhwal United", "Your Brand Here", "Partner With Us"].map((s, i) => (
            <div key={i} className="mx-12 text-3xl font-['Outfit'] font-bold text-[#0B1B3D]/15">{s}</div>
          ))}
        </Marquee>
      </div>
      <div className="text-center">
        <Button data-testid="become-sponsor-button" onClick={() => document.getElementById("get-involved")?.scrollIntoView({ behavior: "smooth" })}
          className="bg-[#1f3a1b] hover:bg-[#0B1B3D] text-white rounded-full px-8 py-6 text-lg font-semibold">
          Become a Sponsor
        </Button>
      </div>
    </div>
  </section>
);

/* ─── GALLERY ─── */
const Gallery = () => {
  const images = [
    "https://images.unsplash.com/photo-1525088068454-ff2c453e50e9?w=600&q=80",
    "https://images.unsplash.com/photo-1526232636376-53d03f24f092?w=600&q=80",
    "https://images.unsplash.com/photo-1761258772213-49262ad73ec0?w=600&q=80",
    "https://images.unsplash.com/photo-1771344164616-3582e4dc2f07?w=600&q=80",
    "https://images.unsplash.com/photo-1704313097739-082bc2e8c945?w=600&q=80",
  ];

  return (
    <section data-testid="gallery-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-16">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#1f3a1b]">Gallery</p>
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight text-[#0B1B3D] mt-4">Moments & Memories</h2>
        </motion.div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {images.map((img, i) => (
            <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, delay: i * 0.05 }} viewport={{ once: true }}
              className="relative aspect-square rounded-2xl overflow-hidden shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <img src={img} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── CONTACT ─── */
const ContactUs = () => (
  <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#F4F7F4] grass-pattern">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center">
        <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#1f3a1b]">Reach Out</p>
        <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight text-[#0B1B3D] mt-4">Contact Us</h2>
        <p className="text-base md:text-lg leading-relaxed text-[#4B5563] mt-6 max-w-2xl mx-auto mb-12">
          Have questions about the tournament? Want to register, volunteer, or partner with us? Get in touch directly.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <motion.a href="tel:+917827938880" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} viewport={{ once: true }}
            className="bg-white rounded-3xl p-8 text-center hover:shadow-lg transition-all duration-300 block border border-[#1f3a1b]/5" data-testid="contact-phone">
            <div className="w-16 h-16 bg-[#1f3a1b] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
            </div>
            <h3 className="font-['Outfit'] text-xl font-semibold text-[#0B1B3D] mb-2">Phone</h3>
            <p className="text-lg text-[#4B5563]">+91 7827938880</p>
          </motion.a>
          <motion.a href="mailto:ballandbeyondfoundation@gmail.com" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} viewport={{ once: true }}
            className="bg-white rounded-3xl p-8 text-center hover:shadow-lg transition-all duration-300 block border border-[#1f3a1b]/5" data-testid="contact-email">
            <div className="w-16 h-16 bg-[#1f3a1b] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            </div>
            <h3 className="font-['Outfit'] text-xl font-semibold text-[#0B1B3D] mb-2">Email</h3>
            <p className="text-lg text-[#4B5563]">ballandbeyondfoundation@gmail.com</p>
          </motion.a>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ─── FOOTER ─── */
const Footer = () => (
  <footer data-testid="footer" className="bg-[#0B1B3D] py-12 relative overflow-hidden">
    <div className="absolute inset-0 field-lines opacity-5 pointer-events-none" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center">
        <div className="flex items-center justify-center space-x-3 mb-6">
          <span className="font-['Outfit'] font-extrabold text-3xl text-white">BALL & BEYOND</span>
        </div>
        <p className="text-white/70 mb-2">Dream. Play. Rise.</p>
        <p className="text-white/50 text-sm mb-4">ballandbeyondfoundation@gmail.com | +91 7827938880</p>
        <p className="text-white/40 text-sm">2026. Ball and Beyond.</p>
      </div>
    </div>
  </footer>
);

/* ─── HOME ─── */
const Home = () => (
  <div>
    <Navigation />
    <Hero />
    <About />
    <OpportunityPathway />
    <TournamentFormat />
    <Awards />
    <Mission />
    <Team />
    <GetInvolved />
    <Sponsors />
    <ContactUs />
    <Footer />
    <Toaster />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
